/**
 * MobileBottomNav — Notion/Diligent-style 5-item bottom navigation for
 * authenticated users on phones. Hidden ≥ md.
 *
 * Surfaces live badge counters (open tasks, stuck opps, unread chats)
 * via the shared `useNavBadges` hook so the bar feels like a native app.
 *
 * Behaviour:
 *   • Active route highlighted with brand violet + top-bar accent.
 *   • Respects iOS safe-area-inset-bottom so the bar isn't hidden
 *     behind the home indicator on iPhones.
 *   • Hidden on ≥ md so it never collides with the desktop sidebar.
 *   • Items are role-aware: founders get `My Startup` instead of
 *     `Opportunities` (which they can't access).
 */
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, Briefcase, Network, MessagesSquare, ClipboardList, Rocket } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import useNavBadges from '@/hooks/useNavBadges';

const ITEM_CLS_BASE =
  'flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[9px] uppercase tracking-[0.18em] font-bold transition-colors relative';

function Badge({ count, urgent }) {
  if (!count) return null;
  return (
    <span
      data-testid="mbn-badge"
      className={`absolute -top-0.5 -right-1 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center text-white shadow-md ${
        urgent ? 'bg-rose-500 animate-pulse' : 'bg-[var(--ve-violet-bright)]'
      }`}
      title={`${count} ${urgent ? 'urgent ' : ''}item${count === 1 ? '' : 's'}`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

function NavItem({ to, label, icon: Icon, testid, end = false, badge = 0, urgent = false }) {
  const loc = useLocation();
  const active = end
    ? loc.pathname === to
    : loc.pathname === to || loc.pathname.startsWith(`${to}/`);
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={`${ITEM_CLS_BASE} ${
        active
          ? 'text-[var(--ve-violet-bright)]'
          : 'text-slate-500 dark:text-white/55 hover:text-slate-900 dark:hover:text-white'
      }`}
    >
      <span
        aria-hidden="true"
        className={`absolute top-0 left-1/2 -translate-x-1/2 h-[2px] w-8 rounded-full transition-opacity ${
          active ? 'bg-[var(--ve-violet-bright)] opacity-100' : 'opacity-0'
        }`}
      />
      <span className="relative">
        <Icon size={20} strokeWidth={active ? 2.2 : 1.7} />
        <Badge count={badge} urgent={urgent} />
      </span>
      <span className="leading-tight">{label}</span>
    </NavLink>
  );
}

export default function MobileBottomNav() {
  const { user } = useAuth();
  const isFounder = user?.role === 'founder';
  const badges = useNavBadges();

  return (
    <nav
      data-testid="mobile-bottom-nav"
      aria-label="Primary"
      className="md:hidden fixed bottom-0 inset-x-0 z-40 flex items-stretch border-t bg-white/95 dark:bg-[#0A0A0A]/95 backdrop-blur-md border-slate-200 dark:border-white/10"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <NavItem to="/dashboard" label="Overview" icon={Home} testid="mbn-overview" end />
      {isFounder ? (
        <NavItem to="/dashboard/my-startup" label="Startup" icon={Rocket} testid="mbn-my-startup" />
      ) : (
        <NavItem
          to="/dashboard/opportunities" label="Pipeline" icon={Briefcase}
          testid="mbn-opportunities"
          badge={badges.opportunities_stuck}
        />
      )}
      <NavItem
        to="/dashboard/tasks" label="Tasks" icon={ClipboardList}
        testid="mbn-tasks"
        badge={badges.tasks_open}
        urgent={badges.tasks_overdue > 0}
      />
      <NavItem to="/dashboard/shura" label="Shura" icon={Network} testid="mbn-shura" />
      <NavItem
        to="/dashboard/chat" label="Chat" icon={MessagesSquare}
        testid="mbn-chat"
        badge={badges.chat_unread}
      />
    </nav>
  );
}
